import { Col, Radio, Row, Select, message } from 'antd'
import React, { Suspense, lazy, useState, useEffect } from 'react'
import { IoLocationSharp } from "react-icons/io5";
import { FaCalendarAlt, FaCarSide } from "react-icons/fa";
import { MdAccessTime } from "react-icons/md";
import { FaVanShuttle } from "react-icons/fa6";
import './index.css'
import dayjs from 'dayjs';
import { combineDateTimeForSearchApi, dayjsFromTimeString } from '../../Config/utils/dateUtils';
import LocationPicker from '../locationPicker';
import TourDestinationPicker from '../tourDestinationPicker';
import BasicButton from '../basicButton';
import { useNavigate } from 'react-router-dom';
import useVehicle from '../../Config/hooks/Apis/useVehicle';
import useGeneralStore from '../../Store/generalStore';
import useCountryStore from '../../Store/countryStore';
import useCurrencyStore from '../../Store/currencyStore';
import { COUNTRIES } from '../countrySelectionModal';
import { isTourEnabled } from '../../Config/utils/featureFlags';
import { buildTourDetailPath } from '../../Config/utils/tourUrlUtils';

const DatePicker = lazy(() =>
    import('antd').then((mod) => ({ default: mod.DatePicker }))
);

const DatePickerFallback = () => (
    <div className='p-2 h-9 outline-none hero-search-control bg-white border border-[#d9d9d9] text-[#bfbfbf] text-sm flex items-center'>
        Select date
    </div>
);

const options = [
    {
        label: (
            <span className='inline-flex items-center gap-2'>
                <FaCarSide aria-hidden />
                Car Rentals
            </span>
        ),
        value: 'cars',
    },
    {
        label: (
            <span className='inline-flex items-center gap-2'>
                <FaVanShuttle aria-hidden />
                Transfers/Things to Do
            </span>
        ),
        value: 'destinations',
    }
];

const dropOffOptions = [
    {
        label: 'No',
        value: 'no',
    },
    {
        label: 'Yes',
        value: 'yes',
    },
]

// Generate time options in 30-minute intervals (00:00 to 23:30)
const generateTimeOptions = () => {
    const options = [];
    for (let hour = 0; hour < 24; hour++) {
        for (let minute = 0; minute < 60; minute += 30) {
            const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
            const displayTime = `${hour}:${minute.toString().padStart(2, '0')}`;
            options.push({
                label: displayTime,
                value: timeString
            });
        }
    }
    return options;
};

const timeOptions = generateTimeOptions();

const disablePastDates = (current) => current && current.isBefore(dayjs(), 'day');

const fieldIconColor = '#4B5563';

const HeroSectionSearchFilters = ({ bgTypeNotifier }) => {

    const [searchType, setSearchType] = useState('cars');
    const [differentDropOff, setDifferentDropOff] = useState('no')
    const [pickupLocation, setPickupLocation] = useState('');
    const [dropoffLocation, setDropoffLocation] = useState('');
    const [fromDate, setFromDate] = useState(null);
    const [fromTime, setFromTime] = useState(() => dayjsFromTimeString('10:00'));
    const [toDate, setToDate] = useState(null);
    const [toTime, setToTime] = useState(() => dayjsFromTimeString('10:00'));

    const [destinationKeyword, setDestinationKeyword] = useState('');
    const [selectedTourDestination, setSelectedTourDestination] = useState(null);

    const navigate = useNavigate()
    const { searchVehicles } = useVehicle();
    const { loading } = useGeneralStore();
    const heroSearchType = useGeneralStore((state) => state.heroSearchType);
    const setHeroSearchType = useGeneralStore((state) => state.setHeroSearchType);
    const focusHeroPickupTick = useGeneralStore((state) => state.focusHeroPickupTick);
    const focusHeroDestinationTick = useGeneralStore((state) => state.focusHeroDestinationTick);
    const selectedCountry = useCountryStore((state) => state.selectedCountry);
    const setSelectedCountry = useCountryStore((state) => state.setSelectedCountry);
    const selectedCurrency = useCurrencyStore((state) => state.selectedCurrency);
    const availableCurrencies = useCurrencyStore((state) => state.availableCurrencies);
    const setCurrencyByCode = useCurrencyStore((state) => state.setCurrencyByCode);
    const tourEnabled = isTourEnabled();
    const searchOptions = tourEnabled ? options : options.filter((option) => option.value !== 'destinations');

    const currencyOptions = (availableCurrencies?.length ? availableCurrencies : [selectedCurrency])
        .filter(Boolean)
        .map((c) => ({
            value: c.currencyCode,
            label: `${c.symbol ? c.symbol + ' ' : ''}${c.currencyCode}`,
        }));

    useEffect(() => {
        const nextType = tourEnabled ? heroSearchType : 'cars';
        setSearchType(nextType);
        bgTypeNotifier(nextType);
    }, [heroSearchType, tourEnabled]);

    useEffect(() => {
        if (!focusHeroPickupTick || searchType !== 'cars') return;

        const timeoutId = window.setTimeout(() => {
            const pickupInput = document.getElementById('hero-pickup-location');
            pickupInput?.focus({ preventScroll: true });
        }, 450);

        return () => window.clearTimeout(timeoutId);
    }, [focusHeroPickupTick, searchType]);

    useEffect(() => {
        if (!focusHeroDestinationTick || searchType !== 'destinations') return;

        const timeoutId = window.setTimeout(() => {
            const destinationInput = document.getElementById('hero-destination-search');
            destinationInput?.focus({ preventScroll: true });
        }, 450);

        return () => window.clearTimeout(timeoutId);
    }, [focusHeroDestinationTick, searchType]);

    const radioChangeHandler = (e) => {
        setSearchType(e?.target.value)
        bgTypeNotifier(e?.target.value)
        setHeroSearchType(e?.target.value)
    }

    const dropOffChangeHandler = (e) => {
        setDifferentDropOff(e?.target.value)
    }

    const datePickerChangeHandler = (date, dateString, type) => {
        if (type === 'from') {
            setFromDate(date);
        } else if (type === 'to') {
            setToDate(date);
        }
    }

    const timePickerChangeHandler = (timeString, type) => {
        const time = dayjsFromTimeString(timeString);
        if (type === 'from') {
            setFromTime(time);
        } else if (type === 'to') {
            setToTime(time);
        }
    }

    const searchCarHandler = async () => {
        // Validate form data
        if (!pickupLocation) {
            message.error('Please select a pick-up location');
            return;
        }
        if (!fromDate || !fromTime) {
            message.error('Please select pick-up date and time');
            return;
        }
        if (!toDate || !toTime) {
            message.error('Please select drop-off date and time');
            return;
        }

        // Local wall-clock (avoid toISOString shifting midnight to UTC, e.g. 00:00 → 19:00Z)
        const fromDateTime = combineDateTimeForSearchApi(fromDate, fromTime);
        const toDateTime = combineDateTimeForSearchApi(toDate, toTime);

        // Prepare search parameters
        const searchParams = {
            fromAddress: pickupLocation,
            toAddress: differentDropOff === 'yes' ? dropoffLocation : pickupLocation,
            fromDateTime: fromDateTime,
            toDateTime: toDateTime,
            pageNumber: 1,
            pageSize: 10,
            filters: null,
        };

        try {
            // Call API
            const vehicles = await searchVehicles(searchParams);

            // Navigate with search data and results
            navigate('/car-listing', {
                state: {
                    searchParams: {
                        fromAddress: pickupLocation,
                        toAddress: differentDropOff === 'yes' ? dropoffLocation : pickupLocation,
                        fromDateTime: fromDateTime,
                        toDateTime: toDateTime,
                        countryCode: selectedCountry?.code ?? null,
                        rentalDays: vehicles.rentalDays ?? null,
                    },
                    vehicles: vehicles.vehicles || [],
                }
            });
        } catch (error) {
            // Error is already handled in useVehicle hook
            console.error('Search error:', error);
        }
    }

    const searchDestinationHandler = () => {
        const params = new URLSearchParams();

        // Picked a specific tour experience → go straight to detail
        if (selectedTourDestination?.type === 'tour' && selectedTourDestination.id) {
            navigate(buildTourDetailPath(selectedTourDestination));
            return;
        }

        if (selectedTourDestination?.type === 'destination' && selectedTourDestination.name) {
            params.set('destination', selectedTourDestination.name);
        } else if (destinationKeyword?.trim()) {
            params.set('keyword', destinationKeyword.trim());
        }

        const query = params.toString();
        navigate(query ? `/destination-listing?${query}` : '/destination-listing', {
            state: {
                keyword: destinationKeyword?.trim() || '',
                destination: selectedTourDestination?.type === 'destination'
                    ? selectedTourDestination.name
                    : null,
            },
        });
    }


    return (
        <Row className='flex flex-col gap-8 max-w-[1100px] w-full px-4 sm:px-8 hero-search-root'>
            <Col lg={24} md={24} sm={24} xs={0} className='hidden flex-col items-center gap-2 sm:flex pt-4'>
                <p className='hero-eyebrow text-center text-white font-bold text-xl md:text-2xl lg:text-[1.75rem] leading-tight custom-heading-shadow'>
                    Car Rentals, Airport Transfers &amp; Tours.
                </p>
                <h2 className='hero-title text-center font-extrabold text-white text-4xl md:text-5xl lg:text-[3.25rem] leading-tight custom-heading-shadow'>
                    Start Your Adventure
                </h2>
                <p className='text-center text-white text-sm md:text-base mt-1 custom-desc-shadow'>
                    {searchType == 'cars' ? (
                        <>Luxury Cars at low-cost, starts at $65 per day at premium locations</>
                    ) : (
                        <>
                            Where adventure meets comfort. We create unforgettable travel
                            <br />
                            experiences
                        </>
                    )}
                </p>
            </Col>
            <Col lg={24} className='flex items-center justify-center flex-col w-full'>
                <div className='hero-search-widget w-full'>
                    <div className='radio-parent-class hero-search-tabs'>
                        <Radio.Group
                            options={searchOptions}
                            value={searchType}
                            defaultValue="cars"
                            optionType="button"
                            buttonStyle="solid"
                            className='w-max max-w-full font-semibold search-type-radio'
                            onChange={radioChangeHandler}
                        />
                    </div>
                    {
                        searchType == 'cars' ?
                            <Row className='w-full hero-search-banner p-4 sm:p-5 flex' justify={'space-between'}>
                                <Col lg={differentDropOff == 'yes' ? 5 : 11} md={differentDropOff == 'yes' ? 11 : 24} sm={differentDropOff == 'yes' ? 11 : 24} xs={differentDropOff == 'yes' ? 24 : 24} className='flex flex-col gap-2 mb-4 lg:mb-0'>
                                    <span className='hero-field-label font-semibold flex items-center gap-1'>
                                        <IoLocationSharp size={15} color={fieldIconColor} className='inline' />
                                        Pick-up Location
                                    </span>
                                    <div className='relative'>
                                        <LocationPicker
                                            id='hero-pickup-location'
                                            value={pickupLocation}
                                            onChange={setPickupLocation}
                                            inputClasses='hero-search-input'
                                        />
                                    </div>
                                </Col>
                                {
                                    differentDropOff == 'yes' &&
                                    <Col lg={5} md={11} sm={11} xs={24} className='flex flex-col gap-2 mb-4 lg:mb-0'>
                                        <span className='hero-field-label font-semibold flex items-center gap-1'>
                                            <IoLocationSharp size={15} color={fieldIconColor} className='inline' />
                                            Drop-off Location
                                        </span>
                                        <div className='relative'>
                                            <LocationPicker
                                                value={dropoffLocation}
                                                onChange={setDropoffLocation}
                                                inputClasses='hero-search-input'
                                            />
                                        </div>
                                    </Col>
                                }
                                <Col lg={4} md={5} sm={5} xs={11} className='flex flex-col gap-2 mb-4 lg:mb-0'>
                                    <span className='hero-field-label font-semibold flex items-center gap-1'>
                                        <FaCalendarAlt size={15} color={fieldIconColor} className='inline' />
                                        Date
                                    </span>
                                    <Suspense fallback={<DatePickerFallback />}>
                                        <DatePicker
                                            disabledDate={disablePastDates}
                                            onChange={(date) => datePickerChangeHandler(date, null, 'from')}
                                            placeholder='From'
                                            className='p-2 h-9 outline-none hero-search-control hover:outline-none'
                                            value={fromDate}
                                        />
                                    </Suspense>
                                </Col>
                                <Col lg={2} md={5} sm={5} xs={11} className='flex flex-col gap-2 mb-4 lg:mb-0'>
                                    <span className='hero-field-label font-semibold flex items-center gap-1'>
                                        <MdAccessTime size={15} color={fieldIconColor} className='inline' />
                                        Time
                                    </span>
                                    <Select
                                        placeholder='From'
                                        className='h-9 hero-search-select'
                                        value={fromTime ? fromTime.format('HH:mm') : null}
                                        onChange={(value) => timePickerChangeHandler(value, 'from')}
                                        options={timeOptions}
                                        showSearch
                                        filterOption={(input, option) =>
                                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                        }
                                    />
                                </Col>
                                <Col lg={4} md={5} sm={5} xs={11} className='flex flex-col gap-2 mb-4 lg:mb-0'>
                                    <span className='hero-field-label font-semibold flex items-center gap-1'>
                                        <FaCalendarAlt size={15} color={fieldIconColor} className='inline' />
                                        Date
                                    </span>
                                    <Suspense fallback={<DatePickerFallback />}>
                                        <DatePicker
                                            disabledDate={disablePastDates}
                                            onChange={(date) => datePickerChangeHandler(date, null, 'to')}
                                            placeholder='To'
                                            className='p-2 h-9 outline-none hero-search-control hover:outline-none'
                                            value={toDate}
                                        />
                                    </Suspense>
                                </Col>
                                <Col lg={2} md={5} sm={5} xs={11} className='flex flex-col gap-2 mb-4 lg:mb-0'>
                                    <span className='hero-field-label font-semibold flex items-center gap-1'>
                                        <MdAccessTime size={15} color={fieldIconColor} className='inline' />
                                        Time
                                    </span>
                                    <Select
                                        placeholder='To'
                                        className='h-9 hero-search-select'
                                        value={toTime ? toTime.format('HH:mm') : null}
                                        onChange={(value) => timePickerChangeHandler(value, 'to')}
                                        options={timeOptions}
                                        showSearch
                                        filterOption={(input, option) =>
                                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                        }
                                    />
                                </Col>
                                <Col span={24} className='mt-4 flex items-center justify-center sm:justify-between flex-wrap gap-4'>
                                    <div className='flex items-center gap-3 drop-off-radio-parent-class hero-dropoff-toggle'>
                                        <p className='hero-utility-text text-xs sm:text-[14px] m-0'>Different Drop-off Location?</p>
                                        <Radio.Group
                                            block
                                            options={dropOffOptions}
                                            value={differentDropOff}
                                            defaultValue="no"
                                            optionType="button"
                                            buttonStyle="solid"
                                            className='w-28 font-semibold'
                                            onChange={dropOffChangeHandler}
                                        />
                                    </div>
                                    <div className='flex items-center gap-3 flex-wrap justify-center sm:justify-end'>
                                        <div className='flex items-center gap-2'>
                                            <Select
                                                value={selectedCurrency?.currencyCode || 'USD'}
                                                options={currencyOptions}
                                                onChange={(code) => setCurrencyByCode(code)}
                                                popupMatchSelectWidth={false}
                                                className='currency-search-select min-w-[72px]'
                                                size='middle'
                                                aria-label='Select currency'
                                            />
                                            <span className='hero-utility-text text-[11px] sm:text-xs whitespace-nowrap'>I live in:</span>
                                            <Select
                                                value={selectedCountry?.code || null}
                                                onChange={(value) => {
                                                    const country = COUNTRIES.find(c => c.code === value);
                                                    if (country) {
                                                        setSelectedCountry(country);
                                                    }
                                                }}
                                                placeholder="Select country"
                                                className='i-live-in-select min-w-[100px]'
                                                showSearch
                                                filterOption={(input, option) =>
                                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                                }
                                                options={COUNTRIES.map(country => ({
                                                    value: country.code,
                                                    label: country.name,
                                                }))}
                                            />
                                        </div>
                                        <BasicButton
                                            text={loading ? 'Searching...' : 'Search Car'}
                                            basicButtonHandler={() => searchCarHandler()}
                                            disabled={loading}
                                            className='hero-search-cta'
                                        />
                                    </div>
                                </Col>
                            </Row>
                            :
                            <Row className='w-full hero-search-banner p-4 sm:p-5 flex justify-between' justify={'space-between'}>
                                <Col lg={24} className='w-full flex gap-3 mb-0 items-end flex-wrap'>
                                    <div className='flex flex-col flex-1 gap-2 min-w-[200px]'>
                                        <span className='hero-field-label font-semibold flex items-center gap-1'>
                                            <IoLocationSharp size={15} color={fieldIconColor} className='inline' />
                                            NearBy
                                        </span>
                                        <div className='relative'>
                                            <TourDestinationPicker
                                                id='hero-destination-search'
                                                placeholder={'Attraction, activity or destination'}
                                                value={destinationKeyword}
                                                onChange={(val) => setDestinationKeyword(val || '')}
                                                onSelectDestination={setSelectedTourDestination}
                                                inputClasses='hero-search-input'
                                            />
                                        </div>
                                    </div>
                                    <div className='flex items-center gap-3 flex-wrap justify-center sm:justify-end'>
                                        <BasicButton
                                            text='Search'
                                            basicButtonHandler={() => searchDestinationHandler()}
                                            className='hero-search-cta'
                                        />
                                    </div>
                                </Col>
                            </Row>
                    }
                </div>
            </Col>
        </Row>
    )
}

export default HeroSectionSearchFilters
